import { ref, onUnmounted } from 'vue'
import type { CockpitActivityItem, ActivityType } from '@/types/cockpit'
import { fetchCockpitActivity } from '@/api/cockpit'

const POLL_INTERVAL_MS = 60_000

export function useActivityFeed() {
  const items = ref<CockpitActivityItem[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  const typeFilter = ref<ActivityType | null>(null)
  const lastUpdatedAt = ref<Date | null>(null)
  const hasMore = ref(false)
  const nextCursor = ref<string | null>(null)

  // Polling bookkeeping
  let intervalId: ReturnType<typeof setInterval> | null = null
  const isPaused = ref(false)

  // Fetch the latest page (head), replacing current items.
  async function fetchLatest(): Promise<void> {
    if (loading.value) return
    loading.value = true
    // Keep error visible but do not blank items (keep stale if poll fails).
    const wasEmpty = items.value.length === 0
    try {
      const response = await fetchCockpitActivity({
        type: typeFilter.value,
        limit: 25,
      })
      items.value = response.data
      nextCursor.value = response.meta.next_cursor
      hasMore.value = response.meta.next_cursor !== null
      lastUpdatedAt.value = new Date()
      // Clear error only on success
      error.value = null
    } catch {
      if (wasEmpty) {
        error.value = 'admin.cockpit.activity.error'
      } else {
        // Keep stale items, surface the refresh-failed error
        error.value = 'admin.cockpit.activity.error_refresh'
      }
    } finally {
      loading.value = false
    }
  }

  // Append older items using the cursor.
  async function loadOlder(): Promise<void> {
    if (loading.value || !nextCursor.value) return
    loading.value = true
    try {
      const response = await fetchCockpitActivity({
        cursor: nextCursor.value,
        type: typeFilter.value,
        limit: 25,
      })
      items.value = [...items.value, ...response.data]
      nextCursor.value = response.meta.next_cursor
      hasMore.value = response.meta.next_cursor !== null
    } catch {
      // Loading older failed — keep what we have
    } finally {
      loading.value = false
    }
  }

  function setFilter(type: ActivityType | null): void {
    typeFilter.value = type
    items.value = []
    nextCursor.value = null
    hasMore.value = false
    void fetchLatest()
  }

  function startPolling(): void {
    if (intervalId !== null) return
    intervalId = setInterval(() => {
      if (!isPaused.value) {
        void fetchLatest()
      }
    }, POLL_INTERVAL_MS)
  }

  function stopPolling(): void {
    if (intervalId !== null) {
      clearInterval(intervalId)
      intervalId = null
    }
  }

  function pause(): void {
    isPaused.value = true
  }

  function resume(): void {
    isPaused.value = false
  }

  // Clean up the interval when the component using this composable is destroyed.
  onUnmounted(() => {
    stopPolling()
  })

  return {
    items,
    loading,
    error,
    typeFilter,
    lastUpdatedAt,
    hasMore,
    fetchLatest,
    loadOlder,
    setFilter,
    startPolling,
    stopPolling,
    pause,
    resume,
  }
}
